import { Check, X, ShieldOff, PenLine, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { FButton } from "@/components/common/FButton";
import { CTASection } from "@/components/common/CTASection";
import { IMAGES, CREATOR_AUDIENCE, CREATOR_HELPS, CREATOR_DOESNT, OWNERSHIP_RULES } from "@/data/content";

export default function Creators() {
  return (
    <div data-testid="creators-page">
      <PageHeader
        overline="For Creators"
        titleLines={["Built for creators", "who want control."]}
        lead="FanFuel is for Caribbean creators ready to turn attention into income — with support, structure and safeguards that keep you in charge."
        testid="creators-header"
      >
        <FButton to="/apply" data-testid="creators-apply-btn">
          Apply to FanFuel <ArrowRight className="h-4 w-4" />
        </FButton>
      </PageHeader>

      {/* audience */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading overline="Who it's for" title="Creators with an audience and ambition." />
            <ul className="mt-8 space-y-4">
              {CREATOR_AUDIENCE.map((item, i) => (
                <Reveal key={item} delay={i * 0.06}>
                  <li className="flex items-start gap-3 rounded-xl border border-white/5 bg-ff-slate/50 p-4">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-ff-ember/15 text-ff-ember">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm text-ff-ivory md:text-base">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="overflow-hidden rounded-[2rem] border border-white/10">
              <img src={IMAGES.portrait} alt="A Caribbean creator" className="h-[460px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ff-midnight/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* helps vs doesn't */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-ff-teal/20 bg-ff-teal/[0.05] p-8 md:p-10" data-testid="creators-helps">
              <h3 className="font-display text-2xl font-semibold text-ff-ivory">What FanFuel helps with</h3>
              <ul className="mt-6 space-y-4">
                {CREATOR_HELPS.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="mt-1 h-4 w-4 flex-shrink-0 text-ff-teal" />
                    <span className="text-sm leading-relaxed text-ff-grey md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-ff-red/20 bg-ff-red/[0.05] p-8 md:p-10" data-testid="creators-doesnt">
              <h3 className="font-display text-2xl font-semibold text-ff-ivory">What FanFuel does not do</h3>
              <ul className="mt-6 space-y-4">
                {CREATOR_DOESNT.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <X className="mt-1 h-4 w-4 flex-shrink-0 text-ff-red" />
                    <span className="text-sm leading-relaxed text-ff-grey md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ownership rules */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading overline="Creator ownership rules" title="Your account. Your content. Your decisions." />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {OWNERSHIP_RULES.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-white/5 bg-ff-slate/60 p-7">
                  <h4 className="font-display text-lg font-semibold text-ff-ember">{r.title}</h4>
                  <p className="mt-3 text-sm leading-relaxed text-ff-grey">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* no-password + AI drafts */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-ff-slate/60 p-8 md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ff-ember/15 text-ff-ember">
                <ShieldOff className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-semibold text-ff-ivory">No-password policy</h3>
              <p className="mt-3 text-base leading-relaxed text-ff-grey">
                FanFuel never asks for your OnlyFans, Fansly or any platform password — and never stores
                them. You keep access and ownership of your own accounts, always.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-ff-slate/60 p-8 md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ff-teal/15 text-ff-teal">
                <PenLine className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-semibold text-ff-ivory">AI drafts are drafts</h3>
              <p className="mt-3 text-base leading-relaxed text-ff-grey">
                AI helps you move faster with captions, campaign ideas and calendars. Every output is a
                draft — you decide what actually gets posted. Nothing goes live without your choice.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Ready to launch with control?"
        secondaryLabel="Read the FAQ"
        secondaryTo="/faq"
      />
    </div>
  );
}
